import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Bell } from 'lucide-react';
import { NotificationsPanel } from '@/components/NotificationsPanel';
import { useNotifications } from '@/hooks/useNotifications';
import { useNavHistory } from '@/lib/navHistory';

interface PageHeaderProps {
  section: string;
  // backHref / backLabel describe the page's place in the hierarchy (its
  // parent). They render as the "up" link in the subtitle, NOT as the back arrow.
  backHref?: string;
  backLabel?: string;
  showNotifications?: boolean;
}

export function PageHeader({
  section,
  backHref,
  backLabel,
  showNotifications = true,
}: PageHeaderProps) {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const { count, hasOverdue } = useNotifications();
  const navigate = useNavigate();
  const { previous } = useNavHistory();

  return (
    <>
      <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-lg mx-auto px-4 py-3">
          {/* Row 1: branding + alerts */}
          <div className="flex items-center justify-between">
            <Link to="/" aria-label="wellKept home" className="flex items-center min-w-0">
              <img
                src="/icons/wellkept-wordmark.png"
                alt="wellKept"
                className="h-12 w-auto"
              />
            </Link>
            {showNotifications && (
              <button
                onClick={() => setIsNotificationsOpen(true)}
                className="relative p-2.5 rounded-full bg-card border border-border hover:border-primary/30 transition-all flex-shrink-0"
              >
                <Bell className="w-5 h-5 text-foreground" />
                {count > 0 && (
                  <span
                    className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full border-2 border-card ${
                      hasOverdue
                        ? 'bg-destructive text-destructive-foreground'
                        : 'bg-accent text-accent-foreground'
                    }`}
                  >
                    {count > 9 ? '9+' : count}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Row 2: hierarchy "up" link (left, under logo) + contextual back
              (right, under the alert icon). */}
          <div className="flex items-center justify-between gap-3 mt-2">
            <div className="min-w-0">
              {backHref ? (
                <p className="text-xs text-muted-foreground leading-tight flex items-center gap-1 truncate">
                  <Link to={backHref} className="font-medium hover:text-foreground transition-colors">
                    {backLabel ?? 'Up'}
                  </Link>
                  <ChevronRight className="w-3 h-3 flex-shrink-0" />
                  <span className="text-foreground truncate">{section}</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground leading-tight">{section}</p>
              )}
            </div>
            {previous && (
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs font-medium">{previous.label}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {showNotifications && (
        <NotificationsPanel
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
        />
      )}
    </>
  );
}
